import * as i0 from '@angular/core';
import { Input, ViewEncapsulation, Component, EventEmitter, Output, ContentChildren, NgModule } from '@angular/core';
import { htmlId } from '@engineering11/utility';
import { E11TemplateMap } from '@engineering11/ui-lib/e11-ui-utils';
import { NgForOf, NgClass, NgIf, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as i3 from '@engineering11/ui-lib/e11-badge';
import { E11BadgeModule } from '@engineering11/ui-lib/e11-badge';
import { map, distinctUntilChanged } from 'rxjs/operators';
import * as i1 from '@engineering11/web-utilities';
import * as i2 from '@angular/router';

/**
 * A single tab page. It renders the passed template
 * via the @Input properties by using the ngTemplateOutlet
 * and ngTemplateOutletContext directives.
 */
class E11TabComponent extends E11TemplateMap {
    constructor() {
        super(...arguments);
        this.tabId = htmlId('e11-tab'); // Can be used by e11-tabs to configure a default selected tab
        this.title = '';
        this.contentClassOverrides = '';
        this.disabled = false;
        this._active = false;
    }
    set active(isActive) {
        setTimeout(() => (this._active = isActive)); // Necessary to prevent NG0100 when e11-tabs mutates active state - https://indepth.dev/posts/1001/everything-you-need-to-know-about-the-expressionchangedafterithasbeencheckederror-error
    }
    get active() {
        return this._active;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TabComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11TabComponent, isStandalone: true, selector: "e11-tab", inputs: { tabId: "tabId", title: "title", badgeValue: "badgeValue", contentClassOverrides: "contentClassOverrides", disabled: "disabled", active: "active" }, usesInheritance: true, ngImport: i0, template: `
    <div class="{{ contentClassOverrides }}" [hidden]="!_active">
      <ng-content></ng-content>
    </div>
  `, isInline: true, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TabComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-tab',
                    standalone: true,
                    template: `
    <div class="{{ contentClassOverrides }}" [hidden]="!_active">
      <ng-content></ng-content>
    </div>
  `,
                    encapsulation: ViewEncapsulation.None,
                }]
        }], propDecorators: { tabId: [{
                type: Input
            }], title: [{
                type: Input
            }], badgeValue: [{
                type: Input
            }], contentClassOverrides: [{
                type: Input
            }], disabled: [{
                type: Input
            }], active: [{
                type: Input
            }] } });

var TabsStyle;
(function (TabsStyle) {
    TabsStyle["OUTLINED"] = "outlined";
    TabsStyle["FILLED"] = "filled";
})(TabsStyle || (TabsStyle = {}));

/**
 *
 * A component that manages a set of tabbed panels. This component allows switching between different
 * tabs, each containing distinct content.
 *
 */
class E11TabsComponent {
    /**
     * Sets the selected tab ID
     */
    set selectedTabId(tabId) {
        this._selectedTabId = tabId;
        // don't trigger unless content children have been init
        if (this.tabs) {
            this.selectTabById(tabId);
        }
    }
    /** For async tabs (e.g. ones that depend on permissions or feature flags), this is needed to select the tab from query params once the tabs are init */
    set tabsLoaded(loaded) {
        if (this._tabsLoaded === loaded || !loaded)
            return;
        this._tabsLoaded = loaded;
        // For tabs that are init async, there is a delay between when they can load into the DOM and when Angular recognizes them as a content child, hence 50ms
        setTimeout(() => {
            const qpTab = this.route.snapshot.queryParams[TabQueryParam];
            this.selectTabByTitle(qpTab);
        }, 50);
    }
    constructor(viewportService, router, route) {
        this.viewportService = viewportService;
        this.router = router;
        this.route = route;
        this.showActiveStatus = true;
        this._tabsLoaded = false;
        /**
         * Class overrides for the tabs container.
         */
        this.tabsClassOverrides = '';
        /**
         * Class overrides for individual tabs.
         * Default is an empty string.
         */
        this.tabClassOverrides = '';
        /**
         * Class overrides for the tab titles.
         */
        this.tabTitleOverrides = '';
        /**
         * The style of the tabs.
         * Can be 'OUTLINED' or 'FILLED'.
         */
        this.tabsStyle = TabsStyle.OUTLINED;
        /**
         * The maximum value to be displayed in the badge.
         */
        this.badgeLimit = 99;
        /**
         * Optional input to control query parameter usage.
         */
        this.useQueryParams = false;
        /**
         * Emits an event when the selected tab changes.
         */
        this.selectedTabChanged = new EventEmitter();
        this.showingOptions = false;
        this.TabsStyle = TabsStyle;
        this.isMobile$ = this.viewportService.viewportSizeChanged$.pipe(map((data) => data == 'sm' || data == 'xs'));
    }
    // contentChildren are set
    ngAfterContentInit() {
        if (this.useQueryParams)
            this.listenQueryParams();
        else
            this.selectDefaultTab();
    }
    listenQueryParams() {
        // Process query params after rendering
        const tabParam$ = this.route.queryParams.pipe(map((params) => params[TabQueryParam]), distinctUntilChanged());
        tabParam$.subscribe((tabTitleFromParams) => this.selectTabByTitle(tabTitleFromParams));
    }
    selectTabByTitle(title) {
        const tabToSelect = this.getTabByTitle(title);
        if (tabToSelect)
            this.selectTab(tabToSelect, true);
        else
            this.selectDefaultTab(); // Fallback to default tab if no valid tab found
    }
    selectDefaultTab() {
        let activeTabs = this.tabs.filter((tab) => tab.active);
        if (activeTabs.length)
            return; // If there are active tabs, do nothing
        this.selectTabById(this._selectedTabId); // If no active tabs, activate the tab based on _selectedTabId
    }
    selectTabById(tabId) {
        const tabToSelect = this.getTabById(tabId) ?? this.tabs.first; // falls back on first tab
        this.selectTab(tabToSelect, true);
    }
    /** suppressQueryParam prevents setting the queryParam in place - only the user clicking the tab should set the query-param - so async tab selection can work*/
    selectTab(tab, suppressQueryParam = false) {
        if (tab.active)
            return; // Check if the clicked tab is different from the current active tab
        // Deactivate all tabs
        this.tabs.toArray().forEach((t) => (t.active = false));
        // Activate the clicked tab
        tab.active = true;
        this.currentTabTitle = tab.title;
        this.activeTab = tab;
        // Emit the tab change event and hide options
        this.selectedTabChanged.emit(tab);
        this.showOptions(false);
        // Set query parameters if necessary, only if the user clicked the tab
        if (this.useQueryParams && !suppressQueryParam) {
            this.router
                .navigate([], {
                relativeTo: this.route,
                queryParams: { [TabQueryParam]: tab.title },
                queryParamsHandling: 'merge',
            })
                .then(() => { });
        }
    }
    getTabByTitle(title) {
        return this.tabs.find((tab) => tab.title === title);
    }
    getTabById(tabId) {
        return this.tabs.find((tab) => tab.tabId === tabId);
    }
    showOptions(value) {
        this.showingOptions = value;
    }
    toggleOptions() {
        this.showingOptions = !this.showingOptions;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TabsComponent, deps: [{ token: i1.ViewportService }, { token: i2.Router }, { token: i2.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11TabsComponent, isStandalone: true, selector: "e11-tabs", inputs: { selectedTabId: "selectedTabId", showActiveStatus: "showActiveStatus", tabsLoaded: "tabsLoaded", tabsClassOverrides: "tabsClassOverrides", tabClassOverrides: "tabClassOverrides", tabTitleOverrides: "tabTitleOverrides", tabsStyle: "tabsStyle", badgeLimit: "badgeLimit", useQueryParams: "useQueryParams" }, outputs: { selectedTabChanged: "selectedTabChanged" }, queries: [{ propertyName: "tabs", predicate: E11TabComponent }], ngImport: i0, template: `
    <div class="e11-tabs">
      <div *ngIf="{ isMobile: (isMobile$ | async) } as _" [ngClass]="{ 'e11-relative': _.isMobile }">
        <div
          *ngIf="_.isMobile"
          (click)="toggleOptions()"
          class="e11-tab-select e11-w-full e11-flex e11-bg-skin-grey/20 e11-cursor-pointer e11-text-sm e11-font-bold e11-border-l-2 e11-pl-2 e11-py-2 e11-text-skin-primary-accent e11-border-skin-secondary e11-mb-4"
        >
          <div class="e11-grow e11-truncate">
            {{ currentTabTitle }}
          </div>
          <div class="e11-flex e11-items-center e11-justify-end e11-pr-1">
            <span class="material-icons-outlined">
              {{ showingOptions ? 'expand_less' : 'expand_more' }}
            </span>
          </div>
        </div>
        <nav
          aria-label="Tabs"
          class="e11-flex e11-flex-col sm:e11-flex-row {{ tabsClassOverrides }}"
          [ngClass]="{
            'e11-w-full e11-tab-options e11-flex-col e11-absolute e11-bg-white e11-border e11-border-skin-app-borders e11-border-t-transparent e11-rounded-b-md':
              _.isMobile,
            active: _.isMobile && showingOptions
          }"
        >
          <div
            (click)="!tab.disabled && selectTab(tab)"
            *ngFor="let tab of tabs"
            [ngClass]="{
              'e11-relative': tab.badgeValue,
              'e11-cursor-not-allowed': tab.disabled,
              'e11-cursor-pointer': !tab.disabled,

              'e11-mr-4': tabsStyle === TabsStyle.OUTLINED && !_.isMobile && !tab.disabled && showActiveStatus,
              'e11-border-b-2 e11-border-b-transparent hover:e11-border-b-2 hover:e11-border-skin-grey e11-pb-2':
                tabsStyle === TabsStyle.OUTLINED && !tab.active && !_.isMobile && showActiveStatus,
              'e11-border-b-2 e11-border-skin-secondary e11-pb-2': tabsStyle === TabsStyle.OUTLINED && tab.active && !_.isMobile && showActiveStatus,

              'e11-p-2 e11-rounded-t e11-relative': tabsStyle === TabsStyle.FILLED && !_.isMobile && showActiveStatus,
              'e11-bg-skin-grey/20 e11-text-white': tabsStyle === TabsStyle.FILLED && tab.active && !_.isMobile && showActiveStatus,
              'hover:e11-bg-skin-primary-accent': tabsStyle === TabsStyle.FILLED && !tab.active && !_.isMobile && showActiveStatus,
              'e11-bg-skin-grey/10': tabsStyle === TabsStyle.FILLED && tab.disabled && !_.isMobile && showActiveStatus,

              'hover:e11-bg-skin-grey/20 e11-w-full e11-border-l-2 e11-pl-2 e11-py-2 e11-border-transparent e11-text-sm e11-font-bold': _.isMobile,
              'e11-text-skin-base': _.isMobile && tab.active,
              'e11-text-skin-primary-accent': _.isMobile && !tab.active && !tab.disabled,
              'e11-text-skin-light': _.isMobile && tab.disabled
            }"
            class=" {{ tabClassOverrides }}"
          >
            <ng-container *ngTemplateOutlet="tab.e11TemplateMap['customHeader']"></ng-container>
            <div
              *ngIf="!tab.e11TemplateMap['customHeader']"
              class="e11-relative"
              [ngClass]="{
                'e11-flex e11-gap-1': tabsStyle === TabsStyle.FILLED && !_.isMobile
              }"
            >
              <span
                class="e11-line-clamp-1  e11-relative {{ tabTitleOverrides }}"
                [ngClass]="{
                 'e11-font-bold': _.isMobile,
                  'e11-text-lg e11-font-bold e11-font-secondary': tabsStyle === TabsStyle.OUTLINED && !_.isMobile,
                   'e11-font-primary-semibold': tabsStyle === TabsStyle.FILLED && !_.isMobile,
                   'e11-text-skin-base': tab.active && !_.isMobile && tabsStyle === TabsStyle.OUTLINED,
                    'e11-text-skin-primary-accent': !tab.active && !tab.disabled && !_.isMobile && tabsStyle === TabsStyle.OUTLINED,
                    'e11-text-skin-light': tab.disabled && !_.isMobile && tabsStyle === TabsStyle.OUTLINED,
                }"
              >
                {{ tab.title }} {{ _.isMobile && tab.badgeValue ? '(' + tab.badgeValue + ')' : '' }}
              </span>

              <e11-badge
                *ngIf="tab.badgeValue && !_.isMobile"
                [inline]="tabsStyle === TabsStyle.FILLED"
                [value]="tab.badgeValue"
                [limit]="badgeLimit"
                [containerClass]="tabsStyle === TabsStyle.OUTLINED ? 'e11-right-2.5 e11-absolute e11--top-1' : ''"
              ></e11-badge>
            </div>
          </div>
        </nav>
      </div>
      <ng-container *ngTemplateOutlet="activeTab?.e11TemplateMap['tabContent']"></ng-container>
      <ng-content *ngIf="!activeTab?.e11TemplateMap['tabContent']"></ng-content>
    </div>
  `, isInline: true, styles: [".e11-tabs-custom-select{position:relative;-moz-appearance:none;-webkit-appearance:none;appearance:none;border:none}.e11-tabs-custom-select-icon{top:7px;right:7px;z-index:1;pointer-events:none}.e11-linebreak{line-break:anywhere}.e11-tab-options{opacity:0;z-index:-1}.e11-tab-options.active{opacity:1;z-index:2}\n"], dependencies: [{ kind: "ngmodule", type: FormsModule }, { kind: "ngmodule", type: E11BadgeModule }, { kind: "component", type: i3.E11BadgeComponent, selector: "e11-badge", inputs: ["color", "shape", "size", "inTag", "classOverrides", "class", "posAdjustY", "posAdjustX", "containerClassOverrides", "containerClass", "inline", "titleText", "titleTextPosition", "value", "limit"] }, { kind: "directive", type: NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: AsyncPipe, name: "async" }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TabsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-tabs', standalone: true, imports: [FormsModule, E11BadgeModule, NgForOf, NgClass, NgIf, NgTemplateOutlet, AsyncPipe], template: `
    <div class="e11-tabs">
      <div *ngIf="{ isMobile: (isMobile$ | async) } as _" [ngClass]="{ 'e11-relative': _.isMobile }">
        <div
          *ngIf="_.isMobile"
          (click)="toggleOptions()"
          class="e11-tab-select e11-w-full e11-flex e11-bg-skin-grey/20 e11-cursor-pointer e11-text-sm e11-font-bold e11-border-l-2 e11-pl-2 e11-py-2 e11-text-skin-primary-accent e11-border-skin-secondary e11-mb-4"
        >
          <div class="e11-grow e11-truncate">
            {{ currentTabTitle }}
          </div>
          <div class="e11-flex e11-items-center e11-justify-end e11-pr-1">
            <span class="material-icons-outlined">
              {{ showingOptions ? 'expand_less' : 'expand_more' }}
            </span>
          </div>
        </div>
        <nav
          aria-label="Tabs"
          class="e11-flex e11-flex-col sm:e11-flex-row {{ tabsClassOverrides }}"
          [ngClass]="{
            'e11-w-full e11-tab-options e11-flex-col e11-absolute e11-bg-white e11-border e11-border-skin-app-borders e11-border-t-transparent e11-rounded-b-md':
              _.isMobile,
            active: _.isMobile && showingOptions
          }"
        >
          <div
            (click)="!tab.disabled && selectTab(tab)"
            *ngFor="let tab of tabs"
            [ngClass]="{
              'e11-relative': tab.badgeValue,
              'e11-cursor-not-allowed': tab.disabled,
              'e11-cursor-pointer': !tab.disabled,

              'e11-mr-4': tabsStyle === TabsStyle.OUTLINED && !_.isMobile && !tab.disabled && showActiveStatus,
              'e11-border-b-2 e11-border-b-transparent hover:e11-border-b-2 hover:e11-border-skin-grey e11-pb-2':
                tabsStyle === TabsStyle.OUTLINED && !tab.active && !_.isMobile && showActiveStatus,
              'e11-border-b-2 e11-border-skin-secondary e11-pb-2': tabsStyle === TabsStyle.OUTLINED && tab.active && !_.isMobile && showActiveStatus,

              'e11-p-2 e11-rounded-t e11-relative': tabsStyle === TabsStyle.FILLED && !_.isMobile && showActiveStatus,
              'e11-bg-skin-grey/20 e11-text-white': tabsStyle === TabsStyle.FILLED && tab.active && !_.isMobile && showActiveStatus,
              'hover:e11-bg-skin-primary-accent': tabsStyle === TabsStyle.FILLED && !tab.active && !_.isMobile && showActiveStatus,
              'e11-bg-skin-grey/10': tabsStyle === TabsStyle.FILLED && tab.disabled && !_.isMobile && showActiveStatus,

              'hover:e11-bg-skin-grey/20 e11-w-full e11-border-l-2 e11-pl-2 e11-py-2 e11-border-transparent e11-text-sm e11-font-bold': _.isMobile,
              'e11-text-skin-base': _.isMobile && tab.active,
              'e11-text-skin-primary-accent': _.isMobile && !tab.active && !tab.disabled,
              'e11-text-skin-light': _.isMobile && tab.disabled
            }"
            class=" {{ tabClassOverrides }}"
          >
            <ng-container *ngTemplateOutlet="tab.e11TemplateMap['customHeader']"></ng-container>
            <div
              *ngIf="!tab.e11TemplateMap['customHeader']"
              class="e11-relative"
              [ngClass]="{
                'e11-flex e11-gap-1': tabsStyle === TabsStyle.FILLED && !_.isMobile
              }"
            >
              <span
                class="e11-line-clamp-1  e11-relative {{ tabTitleOverrides }}"
                [ngClass]="{
                 'e11-font-bold': _.isMobile,
                  'e11-text-lg e11-font-bold e11-font-secondary': tabsStyle === TabsStyle.OUTLINED && !_.isMobile,
                   'e11-font-primary-semibold': tabsStyle === TabsStyle.FILLED && !_.isMobile,
                   'e11-text-skin-base': tab.active && !_.isMobile && tabsStyle === TabsStyle.OUTLINED,
                    'e11-text-skin-primary-accent': !tab.active && !tab.disabled && !_.isMobile && tabsStyle === TabsStyle.OUTLINED,
                    'e11-text-skin-light': tab.disabled && !_.isMobile && tabsStyle === TabsStyle.OUTLINED,
                }"
              >
                {{ tab.title }} {{ _.isMobile && tab.badgeValue ? '(' + tab.badgeValue + ')' : '' }}
              </span>

              <e11-badge
                *ngIf="tab.badgeValue && !_.isMobile"
                [inline]="tabsStyle === TabsStyle.FILLED"
                [value]="tab.badgeValue"
                [limit]="badgeLimit"
                [containerClass]="tabsStyle === TabsStyle.OUTLINED ? 'e11-right-2.5 e11-absolute e11--top-1' : ''"
              ></e11-badge>
            </div>
          </div>
        </nav>
      </div>
      <ng-container *ngTemplateOutlet="activeTab?.e11TemplateMap['tabContent']"></ng-container>
      <ng-content *ngIf="!activeTab?.e11TemplateMap['tabContent']"></ng-content>
    </div>
  `, encapsulation: ViewEncapsulation.None, styles: [".e11-tabs-custom-select{position:relative;-moz-appearance:none;-webkit-appearance:none;appearance:none;border:none}.e11-tabs-custom-select-icon{top:7px;right:7px;z-index:1;pointer-events:none}.e11-linebreak{line-break:anywhere}.e11-tab-options{opacity:0;z-index:-1}.e11-tab-options.active{opacity:1;z-index:2}\n"] }]
        }], ctorParameters: () => [{ type: i1.ViewportService }, { type: i2.Router }, { type: i2.ActivatedRoute }], propDecorators: { tabs: [{
                type: ContentChildren,
                args: [E11TabComponent]
            }], selectedTabId: [{
                type: Input
            }], showActiveStatus: [{
                type: Input
            }], tabsLoaded: [{
                type: Input
            }], tabsClassOverrides: [{
                type: Input
            }], tabClassOverrides: [{
                type: Input
            }], tabTitleOverrides: [{
                type: Input
            }], tabsStyle: [{
                type: Input
            }], badgeLimit: [{
                type: Input
            }], useQueryParams: [{
                type: Input
            }], selectedTabChanged: [{
                type: Output
            }] } });
// To ensure if it gets changed, it's changed in all places it's needed
const TabQueryParam = 'tab';

class E11TabsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11TabsModule, imports: [E11TabsComponent, E11TabComponent], exports: [E11TabComponent, E11TabsComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TabsModule, imports: [E11TabsComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TabsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11TabsComponent, E11TabComponent],
                    exports: [E11TabComponent, E11TabsComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11TabComponent, E11TabsComponent, E11TabsModule, TabsStyle };
